"use client";

import { useEffect } from "react";
import { resolveActiveCatalogueId } from "@/lib/catalogue-selection";
import { useCatalogues } from "@/hooks/use-catalogues";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

interface CatalogueSelectorProps {
  value?: string;
  onValueChange: (value: string) => void;
}

export function CatalogueSelector({ value, onValueChange }: CatalogueSelectorProps) {
  const { catalogues, isLoading, isError } = useCatalogues();

  useEffect(() => {
    if (isLoading || isError) return;
    const fallback = resolveActiveCatalogueId(catalogues, value) || "";
    if ((value || "") !== fallback) {
      onValueChange(fallback);
    }
  }, [catalogues, value, onValueChange, isLoading, isError]);

  if (isLoading) {
    return <div className="w-[200px] h-10 flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  }

  if (isError) {
    return <p role="alert" className="text-sm text-destructive">Could not load catalogues. Please refresh to try again.</p>;
  }

  if (catalogues.length <= 1) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">Catalogue:</span>
      <div className="w-[200px]">
        <Select value={value} onValueChange={onValueChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select Catalogue" />
          </SelectTrigger>
          <SelectContent>
            {catalogues.map((catalogue) => (
              <SelectItem key={catalogue.id} value={catalogue.id}>
                {catalogue.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
